import { NavLink } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PlatformNavSection } from "@/lib/platformNavigation";
import bigLogo from "@/assets/big-logo.png";

function PlatformNavList({
  sections,
  onNavigate,
}: {
  sections: PlatformNavSection[];
  onNavigate?: () => void;
}) {
  return (
    <nav aria-label="Platform administration navigation" className="space-y-5">
      {sections.map((section) => (
        <div key={section.label}>
          <p className="mb-1.5 px-3 font-body text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#8a938d]">
            {section.label}
          </p>
          <div className="space-y-1">
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "group flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 font-heading text-sm font-bold transition-colors",
                      isActive
                        ? "bg-[#174f39] text-white shadow-[0_6px_18px_rgba(23,79,57,0.16)]"
                        : "text-[#536159] hover:bg-[#eef4ef] hover:text-[#174f39]"
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function PlatformIdentity({ roleLabel }: { roleLabel: string }) {
  return (
    <div className="border-b border-[#e0e5df] px-5 py-5">
      <img src={bigLogo} alt="BiG Impact Group" className="h-7 w-auto" />
      <div className="mt-3">
        <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#277454]">Client Portal</p>
        <p className="mt-0.5 font-heading text-lg font-bold leading-snug text-[#17221c]">Platform Administration</p>
      </div>
      <p className="mt-3 font-body text-xs font-semibold text-[#647068]">{roleLabel}</p>
    </div>
  );
}

function PlatformUserFooter({
  name,
  email,
  versionLabel,
  onSignOut,
}: {
  name?: string;
  email?: string;
  versionLabel?: string;
  onSignOut: () => void;
}) {
  return (
    <div className="border-t border-[#e0e5df] px-4 py-4">
      <div className="mb-3 min-w-0 px-1">
        {name ? <p className="truncate font-heading text-sm font-bold text-[#17221c]">{name}</p> : null}
        <p className="truncate font-body text-xs text-[#647068]">{email}</p>
      </div>
      <button
        type="button"
        onClick={onSignOut}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-body text-sm font-semibold text-[#536159] transition-colors hover:bg-[#eef4ef] hover:text-[#174f39]"
      >
        <LogOut className="h-4 w-4" /> Sign out
      </button>
      {versionLabel ? <p className="mt-2 px-1 font-body text-[10px] text-[#8a938d]">{versionLabel}</p> : null}
    </div>
  );
}

export interface PlatformPortalNavigationViewProps {
  roleLabel: string;
  userName?: string;
  userEmail?: string;
  versionLabel?: string;
  sections: PlatformNavSection[];
  mobileOpen: boolean;
  onMobileToggle: () => void;
  onSignOut: () => void;
  previewMobile?: boolean;
}

export function PlatformPortalNavigationView({
  roleLabel,
  userName,
  userEmail,
  versionLabel,
  sections,
  mobileOpen,
  onMobileToggle,
  onSignOut,
  previewMobile = false,
}: PlatformPortalNavigationViewProps) {
  return (
    <>
      <aside className={previewMobile ? "hidden" : "hidden h-full w-64 shrink-0 flex-col border-r border-[#d9ded8] bg-[#fffefb] lg:flex"}>
        <PlatformIdentity roleLabel={roleLabel} />
        <div className="flex-1 overflow-y-auto px-3 py-5">
          <PlatformNavList sections={sections} />
        </div>
        <PlatformUserFooter name={userName} email={userEmail} versionLabel={versionLabel} onSignOut={onSignOut} />
      </aside>

      <div className={previewMobile ? "w-full max-w-sm border border-[#d9ded8] bg-[#fffefb]" : "border-b border-[#d9ded8] bg-[#fffefb] lg:hidden"}>
        <div className="flex min-h-16 items-center justify-between gap-3 px-4 py-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <img src={bigLogo} alt="BiG Impact Group" className="h-5 w-auto shrink-0" />
            <div className="min-w-0">
              <p className="font-body text-[9px] font-extrabold uppercase tracking-[0.15em] text-[#277454]">Client Portal</p>
              <p className="font-heading text-sm font-bold leading-snug text-[#17221c]">Platform Administration</p>
            </div>
          </div>
          <button
            type="button"
            aria-expanded={mobileOpen}
            aria-controls="platform-portal-mobile-navigation"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            onClick={onMobileToggle}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#ccd5ce] text-[#174f39]"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileOpen ? (
          <div id="platform-portal-mobile-navigation" className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-[#e0e5df] px-4 py-4">
            <p className="mb-3 font-body text-xs font-semibold text-[#647068]">{roleLabel}</p>
            <PlatformNavList sections={sections} onNavigate={onMobileToggle} />
            <div className="mt-4 border-t border-[#e0e5df] pt-3">
              <PlatformUserFooter name={userName} email={userEmail} versionLabel={versionLabel} onSignOut={onSignOut} />
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
